import { useState } from "react";
import { Dialog } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/auth-context";
import Logo from "../logo-removebg-preview.png";
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Button,
  CircularProgress,
} from "@nextui-org/react";

const navigation = [
  { name: "صفحه‌ی اصلی", href: "/", internal: true, protected: false },
  { name: "درباره‌ی ما", href: "https://gozinehapp.com", internal: false, protected: false },
  { name: "راهنما", href: "https://gozinehapp.com/help", internal: false, protected: false },
  { name: "بلاگ", href: "https://gozinehapp.com/blog", internal: false, protected: false },
  { name: "تصمیم‌های من", href: "/my-decisions", internal: true, protected: true },
  { name: "ایجاد تصمیم", href: "/problem/type", internal: true, protected: true },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [{ isAuthenticated, user, status }, authDispatch, { canAccessProtectedFeatures }] = useAuth();
  const isLoading = status === "loading";

  const filteredNavigation = navigation.filter(item => !item.protected || isAuthenticated);

  return (
    <header className="z-20">
      <nav
        className="mx-auto flex max-w-7xl items-center justify-between gap-x-6 p-6 lg:px-8"
        aria-label="Global"
      >
        <div className="flex lg:flex-1">
          <Link href="/" className="-m-1.5 p-1.5">
            <span className="sr-only">Your Company</span>
            <img className="h-24 w-auto" src={Logo} alt="" />
          </Link>
        </div>
        <div className="hidden lg:flex lg:gap-x-12 z-20">
          {filteredNavigation.map((item) =>
            item.internal ? (
              <Link
                key={item.name}
                to={item.href}
                className="text-sm font-semibold leading-6 text-gray-900 z-20"
              >
                {item.name}
              </Link>
            ) : (
              <a
                href={item.href}
                className="text-sm font-semibold leading-6 text-gray-900 z-20"
              >
                {item.name}
              </a>
            ),
          )}
        </div>
        <div className="flex flex-1 items-center justify-end gap-x-6">
          {isLoading ? (
            <CircularProgress aria-label="Loading..." />
          ) : isAuthenticated && user ? (
            <Dropdown>
              <DropdownTrigger>
                <Button variant="bordered">
                  خوش آمدید، {user.first_name}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    class="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                    />
                  </svg>
                </Button>
              </DropdownTrigger>
              <DropdownMenu aria-label="Static Actions">
                <DropdownItem 
                  key="new" 
                  textValue="ایجاد تصمیم جدید"
                  onClick={() => window.location.href = '/problem/type'}
                >
                  ایجاد تصمیم جدید
                </DropdownItem>
                <DropdownItem 
                  key="my-decisions" 
                  textValue="تصمیم‌های من"
                  onClick={() => window.location.href = '/my-decisions'}
                >
                  تصمیم‌های من
                </DropdownItem>
                {
                  user.is_admin
                    ?
                      <DropdownItem 
                        key="users-table" 
                        textValue="کاربران"
                        onClick={() => window.location.href = '/users-table'}
                      >
                        کاربران
                      </DropdownItem>
                    :
                      null
                }
                <DropdownItem
                  key="logout"
                  className="text-danger"
                  color="danger"
                  onClick={() => {
                    authDispatch({ type: "logout" });
                    window.location = "/";
                  }}
                >
                  خروج
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          ) : (
            <>
              <Link
                to="/login"
                className="hidden lg:block lg:text-sm lg:font-semibold lg:leading-6 lg:text-gray-900 z-20"
              >
                ورود
              </Link>
              <Link
                to="/signup"
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 z-20"
              >
                عضویت
              </Link>
            </>
          )}
        </div>
        <div className="flex lg:hidden">
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700 z-10"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </nav>
      <Dialog
        as="div"
        className="lg:hidden"
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        <div className="fixed inset-0 z-10" />
        <Dialog.Panel className="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center gap-x-6">
            <Link to="/" className="-m-1.5 p-1.5">
              <span className="sr-only">کمک به تصمیم‌گیری</span>
              <img className="h-20 w-auto" src={Logo} alt="" />
            </Link>
            {isLoading ? (
              <CircularProgress aria-label="Loading..." />
            ) : isAuthenticated && user ? (
              <Dropdown>
                <DropdownTrigger>
                  <Button variant="bordered">
                    خوش آمدید، {user.first_name}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      class="w-6 h-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                      />
                    </svg>
                  </Button>
                </DropdownTrigger>
                <DropdownMenu aria-label="Static Actions">
                  <DropdownItem 
                    key="new" 
                    textValue="ایجاد تصمیم جدید"
                    onClick={() => window.location.href = '/problem/type'}
                  >
                    ایجاد تصمیم جدید
                  </DropdownItem>
                  <DropdownItem 
                    key="my-decisions" 
                    textValue="تصمیم‌های من"
                    onClick={() => window.location.href = '/my-decisions'}
                  >
                    تصمیم‌های من
                  </DropdownItem>
                  <DropdownItem
                    key="logout"
                    className="text-danger"
                    color="danger"
                    onClick={() => {
                      authDispatch({ type: "logout" });
                      window.location = "/";
                    }}
                  >
                    خروج
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            ) : (
              <Link
                to="/signup"
                className="ml-auto rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                عضویت
              </Link>
            )}
            <button
              type="button"
              className="-m-2.5 rounded-md p-2.5 text-gray-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="space-y-2 py-6">
                {filteredNavigation.map((item) =>
                  item.internal ? (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                    >
                      {item.name}
                    </Link>
                  ) : (
                    <a
                      href={item.href}
                      className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                    >
                      {item.name}
                    </a>
                  ),
                )}
              </div>
              <div className="py-6">
                {!isLoading && !isAuthenticated && (
                  <>
                    <Link
                      to="/login"
                      className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                    >
                      ورود
                    </Link>
                    <Link
                      to="/signup"
                      className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                    >
                      عضویت
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </Dialog>
    </header>
  );
}
